import * as XLSX from 'xlsx';

function sanitizeFileName(name: string) {
  return name.replace(/[^\w\-]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') || 'export';
}

function normalizeExportValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === 'object') {
    if (typeof (value as { toISOString?: () => string }).toISOString === 'function') {
      return (value as Date).toISOString().slice(0, 10);
    }
    return JSON.stringify(value);
  }
  return String(value);
}

export interface ExcelExportColumn<T> {
  header: string;
  getValue: (row: T) => unknown;
}

export function exportRowsToExcel<T>({
  columns,
  rows,
  fileName,
  sheetName = 'Data',
}: {
  columns: ExcelExportColumn<T>[];
  rows: T[];
  fileName: string;
  sheetName?: string;
}) {
  if (!rows.length) return;

  const headers = columns.map((c) => c.header);
  const data = rows.map((row) => {
    const record: Record<string, string> = {};
    columns.forEach((col) => {
      record[col.header] = normalizeExportValue(col.getValue(row));
    });
    return record;
  });

  const worksheet = XLSX.utils.json_to_sheet(data, { header: headers });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.slice(0, 31));
  XLSX.writeFile(workbook, `${sanitizeFileName(fileName)}.xlsx`);
}

export function buildExportColumns<T>(
  columns: Array<{
    id: string;
    header: string;
    exportValue?: (row: T) => unknown;
    searchValue?: (row: T) => string;
  }>
): ExcelExportColumn<T>[] {
  return columns.map((col) => ({
    header: col.header,
    getValue: (row) => {
      if (col.exportValue) return col.exportValue(row);
      if (col.searchValue) return col.searchValue(row);
      const raw = (row as Record<string, unknown>)[col.id];
      return raw;
    },
  }));
}

/** Empty import template with column headers (and optional sample row). */
export function downloadImportTemplate({
  columns,
  fileName,
  sheetName = 'Template',
  includeSampleRow = false,
}: {
  columns: Array<{ id: string; header: string }>;
  fileName: string;
  sheetName?: string;
  includeSampleRow?: boolean;
}) {
  if (!columns.length) return;
  const headers = columns.map((c) => c.header);
  const rows = includeSampleRow
    ? [headers, columns.map(() => '')]
    : [headers];
  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.slice(0, 31));
  XLSX.writeFile(workbook, `${sanitizeFileName(fileName)}-template.xlsx`);
}

export function parseImportFile(file: File): Promise<Record<string, unknown>[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const name = file.name.toLowerCase();
        if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
          const data = new Uint8Array(reader.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
          resolve(json);
          return;
        }
        resolve(parseCSVText(String(reader.result)));
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(reader.error);
    if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  });
}

function parseCSVText(text: string): Record<string, unknown>[] {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
  return lines.slice(1).filter(Boolean).map((line) => {
    const values = line.split(',').map((v) => v.trim().replace(/^"|"$/g, '').replace(/""/g, '"'));
    const row: Record<string, unknown> = {};
    headers.forEach((h, i) => { row[h] = values[i] || ''; });
    return row;
  });
}

/** Map imported header labels (or field keys) to module field names. */
export function normalizeImportRows(
  records: Record<string, unknown>[],
  columns: Array<{ field: string; header: string }>,
): Record<string, unknown>[] {
  const map = new Map<string, string>();
  columns.forEach((col) => {
    map.set(col.field.toLowerCase(), col.field);
    map.set(col.header.toLowerCase(), col.field);
  });
  return records.map((row) => {
    const out: Record<string, unknown> = {};
    Object.entries(row).forEach(([key, value]) => {
      const field = map.get(String(key).trim().toLowerCase()) || key;
      out[field] = value;
    });
    return out;
  });
}
